import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";

// Components
import ScrollToTop from "./components/ScrollToTop";

// Layouts
import PublicLayout from "./components/layout/PublicLayout";
import DashboardLayout from "./components/layout/DashboardLayout";

// Public Pages
import Home from "./pages/public/Home";
import Checkout from "./pages/public/Checkout";
import About from "./pages/public/About";
import Services from "./pages/public/Services";
import LoanProducts from "./pages/public/LoanProducts";
import BecomeAgent from "./pages/public/BecomeAgent";
import Contact from "./pages/public/Contact";
import HomeLoanForm from "./pages/public/HomeLoanForm";
import BusinessLoanForm from "./pages/public/BusinessLoanForm";
import PersonalLoanForm from "./pages/public/PersonalLoanForm";
import CarLoanForm from "./pages/public/CarLoanForm";
import EducationLoanForm from "./pages/public/EducationLoanForm";

// Auth Page
import Login from "./pages/Login";

// Admin Dashboard
import AdminDashboard from "./pages/admin/AdminDashboard";

// Admin Leads
import { AllLeads, FollowUpLeads, ConvertedLeads, PendingLeads, RejectedLeads } from "./pages/admin/LeadsManagement";

// Admin Loans
import { AllLoans, ProcessingLoans, ApprovedLoans, DisbursedLoans, RejectedLoans, HoldLoans } from "./pages/admin/LoansManagement";

// Admin Agents
import { AllAgents, AgentPerformance, AgentTargets, AgentRequest } from "./pages/admin/AgentsManagement";

// Admin Employees
import { AllEmployees, EmployeeActivityLogs } from "./pages/admin/EmployeesManagement";
import RoleAndPermission from "./pages/admin/RoleAndPermission";

// Admin Commissions
import {
  CommissionSlabs,
  CommissionPackageConfigPage,
  PendingPayouts,
  ApprovedPayouts,
  CommissionLedger,
} from "./pages/admin/CommissionManagement";

// Admin Banks
import { LoanSchemes, InterestSetup, ProcessingFees, IntegrationSettings, ViewBankPage } from "./pages/admin/BanksManagement";

// Admin Reports
import { RevenueReport, ConversionReport, AgentWiseReport, BankWiseReport, ExportData } from "./pages/admin/Reports";

// Admin Settings
import { CompanyProfile, RolePermissions, AuditLogs, SecuritySettings } from "./pages/admin/Settings";
import AdminProfile from "./pages/admin/AdminProfile";

// Admin Alerts
import { PendingApprovals, OverdueFiles, HighValueAlerts } from "./pages/admin/Alerts";

// Admin White Label Management
import { AllWhiteLabels, AddWhiteLabel, WLFeaturePermissions, WLCommissionPlans, SuspendedWhiteLabels } from "./pages/admin/WhiteLabelManagement";
import { EditWhiteLabel, ViewWhiteLabel } from "./pages/admin/WhiteLabelDetailsPages";
import { ViewAgentPage, ViewEmployeePage } from "./pages/admin/UserDetailsPages";

// Admin Pricing Plans
import { AllPricingPlans, PlanForm, UpgradePlansPage } from "./pages/admin/PricingPlansManagement";

// White Label Admin Pages
import WhiteLabelDashboard from "./pages/whitelabel/WhiteLabelDashboard";

// Employee Pages
import EmployeeDashboard from "./pages/employee/EmployeeDashboard";
import {
  EmployeeLeads,
  FollowUpLeads as EmployeeFollowUpLeads,
  ConvertedLeads as EmployeeConvertedLeads,
  PendingLeads as EmployeePendingLeads,
  RejectedLeads as EmployeeRejectedLeads,
} from "./pages/employee/EmployeeLeads";
import {
  EmployeeLoans,
  ProcessingLoans as EmployeeProcessingLoans,
  ApprovedLoans as EmployeeApprovedLoans,
  DisbursedLoans as EmployeeDisbursedLoans,
  RejectedLoans as EmployeeRejectedLoans,
} from "./pages/employee/EmployeeLoans";
import EmployeeDocuments from "./pages/employee/EmployeeDocuments";
import EmployeeFollowUps from "./pages/employee/EmployeeFollowUps";
import EmployeePerformanceReport from "./pages/employee/EmployeePerformanceReport";
import EmployeeFilesReport from "./pages/employee/EmployeeFilesReport";
import EmployeeProfile from "./pages/employee/EmployeeProfile";
import EmployeeAlerts from "./pages/employee/EmployeeAlerts";
import EmployeeCommission from "./pages/employee/EmployeeCommission";

// Agent Pages
import AgentDashboard from "./pages/agent/AgentDashboard";
import {
  AgentLeads,
  AddLead as AgentAddLead,
  FollowUpLeads as AgentFollowUpLeads,
  DocsPendingLeads as AgentDocsPendingLeads,
  ApprovedLeads as AgentApprovedLeads,
  RejectedLeads as AgentRejectedLeads
} from "./pages/agent/AgentLeads";
import {
  AgentLoans,
  ProcessingLoans as AgentProcessingLoans,
  ApprovedLoans as AgentApprovedLoans,
  DisbursedLoans as AgentDisbursedLoans,
  RejectedLoans as AgentRejectedLoans
} from "./pages/agent/AgentLoans";
import AgentLoanDetails from "./pages/agent/AgentLoanDetails";
import LoanDetailsPage from "./pages/shared/LoanDetails";
import LeadDetailsPage from "./pages/shared/LeadDetails";
import { RoleFollowUpLeadsPage, RoleFollowUpsPage } from "./pages/shared/RoleFollowUps";
import AgentDocuments from "./pages/agent/AgentDocuments";
import AgentEarnings from "./pages/agent/AgentEarnings";
import AgentPayouts from "./pages/agent/AgentPayouts";
import AgentProfile from "./pages/agent/AgentProfile";
import AgentFollowUps from "./pages/agent/AgentFollowUps";
import AgentNotifications from "./pages/agent/AgentNotifications";

// Agent Forms
import AgentHomeLoan from "./pages/agent/agentforms/AgentHomeLoan";
import AgentBusinessLoan from "./pages/agent/agentforms/AgentBusinessLoan";
import AgentPersonalLoan from "./pages/agent/agentforms/AgentPersonalLoan";
import AgentCarLoan from "./pages/agent/agentforms/AgentCarLoan";
import AgentEducationLoan from "./pages/agent/agentforms/AgentEducationLoan";
import AgentLoanProducts from "./pages/agent/agentforms/AgentLoanProducts";


function App() {
  return (
    <ThemeProvider>
      <Toaster position="top-right" richColors closeButton />
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Public Routes */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/loan-products" element={<LoanProducts />} />
              <Route path="/become-agent" element={<BecomeAgent />} />
              <Route path="/contact" element={<Contact />} />

              {/* Loan Application Forms */}
              <Route path="/apply/home-loan" element={<HomeLoanForm />} />
              <Route path="/apply/business-loan" element={<BusinessLoanForm />} />
              <Route path="/apply/personal-loan" element={<PersonalLoanForm />} />
              <Route path="/apply/car-loan" element={<CarLoanForm />} />
              <Route path="/apply/education-loan" element={<EducationLoanForm />} />
            </Route>

            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />

            {/* Admin Routes */}
            <Route element={<DashboardLayout allowedRoles={["super_admin"]} />}>
              {/* Dashboard */}
              <Route path="/admin/dashboard" element={<AdminDashboard />} />

              {/* Leads */}
              <Route path="/admin/leads" element={<AllLeads />} />
              <Route path="/admin/leads/:leadId" element={<LeadDetailsPage />} />
              <Route path="/admin/leads/followup" element={<FollowUpLeads />} />
              <Route path="/admin/leads/converted" element={<ConvertedLeads />} />
              <Route path="/admin/leads/pending" element={<PendingLeads />} />
              <Route path="/admin/leads/rejected" element={<RejectedLeads />} />

              <Route path="/admin/followups/today" element={<RoleFollowUpsPage />} />
              <Route path="/admin/followups/upcoming" element={<RoleFollowUpsPage />} />
              <Route path="/admin/followups/overdue" element={<RoleFollowUpsPage />} />
              <Route path="/admin/followups/completed" element={<RoleFollowUpsPage />} />

              {/* Loans */}
              <Route path="/admin/loans" element={<AllLoans />} />
              <Route path="/admin/loans/:id" element={<LoanDetailsPage />} />
              <Route path="/admin/loans/processing" element={<ProcessingLoans />} />
              <Route path="/admin/loans/approved" element={<ApprovedLoans />} />
              <Route path="/admin/loans/disbursed" element={<DisbursedLoans />} />
              <Route path="/admin/loans/rejected" element={<RejectedLoans />} />
              <Route path="/admin/loans/hold" element={<HoldLoans />} />
              <Route path="/admin/apply/products" element={<AgentLoanProducts roleBasePath="/admin" />} />
              <Route path="/admin/apply/home-loan" element={<AgentHomeLoan roleBasePath="/admin" />} />
              <Route path="/admin/apply/business-loan" element={<AgentBusinessLoan roleBasePath="/admin" />} />
              <Route path="/admin/apply/personal-loan" element={<AgentPersonalLoan roleBasePath="/admin" />} />
              <Route path="/admin/apply/car-loan" element={<AgentCarLoan roleBasePath="/admin" />} />
              <Route path="/admin/apply/education-loan" element={<AgentEducationLoan roleBasePath="/admin" />} />

              {/* Agents */}
              <Route path="/admin/agents" element={<AllAgents />} />
              <Route path="/admin/agents/:id/view" element={<ViewAgentPage />} />
              <Route path="/admin/agents/performance" element={<AgentPerformance />} />
              <Route path="/admin/agents/commission" element={<Navigate to="/admin/commission/ledger" replace />} />
              <Route path="/admin/agents/targets" element={<AgentTargets />} />
              <Route path="/admin/agents/request" element={<AgentRequest />} />

              {/* Employees */}
              <Route path="/admin/employees" element={<AllEmployees />} />
              <Route path="/admin/employees/:id/view" element={<ViewEmployeePage />} />
              <Route path="/admin/employees/roles" element={<RoleAndPermission />} />
              <Route path="/admin/employees/logs" element={<EmployeeActivityLogs />} />

              {/* Commissions */}
              <Route path="/admin/commission/slabs" element={<CommissionSlabs />} />
              <Route path="/admin/commission/slabs/:packageId" element={<CommissionPackageConfigPage />} />
              <Route path="/admin/commission/pending" element={<PendingPayouts />} />
              <Route path="/admin/commission/approved" element={<ApprovedPayouts />} />
              <Route path="/admin/commission/ledger" element={<CommissionLedger />} />

              {/* Banks */}
              <Route path="/admin/banks" element={<LoanSchemes />} />
              <Route path="/admin/banks/:id/view" element={<ViewBankPage />} />
              {/* <Route path="/admin/banks/interest" element={<InterestSetup />} />
              <Route path="/admin/banks/fees" element={<ProcessingFees />} /> */}
              {/* <Route path="/admin/banks/integration" element={<IntegrationSettings />} /> */}

              {/* Reports */}
              <Route path="/admin/reports/revenue" element={<RevenueReport />} />
              <Route path="/admin/reports/conversion" element={<ConversionReport />} />
              <Route path="/admin/reports/agents" element={<AgentWiseReport />} />
              <Route path="/admin/reports/banks" element={<BankWiseReport />} />

              {/* Admin personal profile & password */}
              <Route path="/admin/profile" element={<AdminProfile />} />
              <Route path="/admin/profile/password" element={<AdminProfile />} />

              {/* Settings */}
              <Route path="/admin/settings/company" element={<CompanyProfile />} />
              <Route path="/admin/settings/permissions" element={<RolePermissions />} />
              <Route path="/admin/settings/audit" element={<AuditLogs />} />
              <Route path="/admin/settings/security" element={<SecuritySettings />} />

              {/* Alerts */}
              <Route path="/admin/alerts/pending" element={<PendingApprovals />} />
              <Route path="/admin/alerts/overdue" element={<OverdueFiles />} />
              <Route path="/admin/alerts/high-value" element={<HighValueAlerts />} />

              {/* White Label Management */}
              <Route path="/admin/whitelabels" element={<AllWhiteLabels />} />
              <Route path="/admin/whitelabels/add" element={<AddWhiteLabel />} />
              <Route path="/admin/whitelabels/:id/view" element={<ViewWhiteLabel />} />
              <Route path="/admin/whitelabels/:id/edit" element={<EditWhiteLabel />} />
              {/* <Route path="/admin/whitelabels/permissions" element={<WLFeaturePermissions />} />
              <Route path="/admin/whitelabels/commission-plans" element={<WLCommissionPlans />} />
              <Route path="/admin/whitelabels/suspended" element={<SuspendedWhiteLabels />} /> */}

              {/* Pricing Plans */}
              <Route path="/admin/plans" element={<AllPricingPlans />} />
              <Route path="/admin/plans/create" element={<PlanForm />} />
              <Route path="/admin/plans/edit/:id" element={<PlanForm />} />
              <Route path="/admin/*" element={<Navigate to="/admin/dashboard" replace />} />
            </Route>

            {/* White Label Admin Routes — same pages as Admin, scoped under /whitelabel/ */}
            <Route element={<DashboardLayout allowedRoles={["wl_admin"]} />}>
              {/* Dashboard */}
              <Route path="/whitelabel/dashboard" element={<WhiteLabelDashboard />} />
              <Route path="/whitelabel/dashboard/kpi" element={<WhiteLabelDashboard />} />
              <Route path="/whitelabel/dashboard/commission" element={<WhiteLabelDashboard />} />
              <Route path="/whitelabel/dashboard/performance" element={<WhiteLabelDashboard />} />
              <Route path="/whitelabel/dashboard/alerts" element={<WhiteLabelDashboard />} />

              {/* Leads */}
              <Route path="/whitelabel/leads" element={<AllLeads />} />
              <Route path="/whitelabel/leads/:leadId" element={<LeadDetailsPage />} />
              <Route path="/whitelabel/leads/followup" element={<RoleFollowUpLeadsPage />} />
              <Route path="/whitelabel/leads/converted" element={<ConvertedLeads />} />
              <Route path="/whitelabel/leads/pending" element={<PendingLeads />} />
              <Route path="/whitelabel/leads/rejected" element={<RejectedLeads />} />
              <Route path="/whitelabel/followups/today" element={<RoleFollowUpsPage />} />
              <Route path="/whitelabel/followups/upcoming" element={<RoleFollowUpsPage />} />
              <Route path="/whitelabel/followups/overdue" element={<RoleFollowUpsPage />} />
              <Route path="/whitelabel/followups/completed" element={<RoleFollowUpsPage />} />

              {/* Loans */}
              <Route path="/whitelabel/loans" element={<AllLoans />} />
              <Route path="/whitelabel/loans/:id" element={<LoanDetailsPage />} />
              <Route path="/whitelabel/loans/processing" element={<ProcessingLoans />} />
              <Route path="/whitelabel/loans/approved" element={<ApprovedLoans />} />
              <Route path="/whitelabel/loans/disbursed" element={<DisbursedLoans />} />
              <Route path="/whitelabel/loans/rejected" element={<RejectedLoans />} />
              <Route path="/whitelabel/loans/hold" element={<HoldLoans />} />
              <Route path="/whitelabel/apply/products" element={<AgentLoanProducts roleBasePath="/whitelabel" />} />
              <Route path="/whitelabel/apply/home-loan" element={<AgentHomeLoan roleBasePath="/whitelabel" />} />
              <Route path="/whitelabel/apply/business-loan" element={<AgentBusinessLoan roleBasePath="/whitelabel" />} />
              <Route path="/whitelabel/apply/personal-loan" element={<AgentPersonalLoan roleBasePath="/whitelabel" />} />
              <Route path="/whitelabel/apply/car-loan" element={<AgentCarLoan roleBasePath="/whitelabel" />} />
              <Route path="/whitelabel/apply/education-loan" element={<AgentEducationLoan roleBasePath="/whitelabel" />} />

              {/* Agents */}
              <Route path="/whitelabel/agents" element={<AllAgents />} />
              <Route path="/whitelabel/agents/:id/view" element={<ViewAgentPage />} />
              <Route path="/whitelabel/agents/performance" element={<AgentPerformance />} />
              <Route path="/whitelabel/agents/commission" element={<Navigate to="/whitelabel/commission/ledger" replace />} />
              <Route path="/whitelabel/agents/targets" element={<AgentTargets />} />

              {/* Employees */}
              <Route path="/whitelabel/employees" element={<AllEmployees />} />
              <Route path="/whitelabel/employees/:id/view" element={<ViewEmployeePage />} />
              <Route path="/whitelabel/employees/roles" element={<RoleAndPermission />} />
              <Route path="/whitelabel/employees/logs" element={<EmployeeActivityLogs />} />

              {/* Commission */}
              <Route path="/whitelabel/commission/slabs" element={<CommissionSlabs />} />
              <Route path="/whitelabel/commission/slabs/:packageId" element={<CommissionPackageConfigPage />} />
              <Route path="/whitelabel/commission/pending" element={<PendingPayouts />} />
              <Route path="/whitelabel/commission/approved" element={<ApprovedPayouts />} />
              <Route path="/whitelabel/commission/ledger" element={<CommissionLedger />} />

              {/* Banks */}
              <Route path="/whitelabel/banks" element={<LoanSchemes />} />
              <Route path="/whitelabel/banks/:id/view" element={<ViewBankPage />} />
              <Route path="/whitelabel/banks/interest" element={<InterestSetup />} />
              <Route path="/whitelabel/banks/fees" element={<ProcessingFees />} />

              {/* Reports */}
              <Route path="/whitelabel/reports/revenue" element={<RevenueReport />} />
              <Route path="/whitelabel/reports/conversion" element={<ConversionReport />} />
              <Route path="/whitelabel/reports/agents" element={<AgentWiseReport />} />
              <Route path="/whitelabel/reports/export" element={<ExportData />} />

              {/* Alerts */}
              <Route path="/whitelabel/alerts/pending" element={<PendingApprovals />} />
              <Route path="/whitelabel/alerts/overdue" element={<OverdueFiles />} />
              <Route path="/whitelabel/alerts/high-value" element={<HighValueAlerts />} />

              {/* Branding & Profile — use Settings pages */}
              <Route path="/whitelabel/branding/logo" element={<CompanyProfile />} />
              <Route path="/whitelabel/branding/name" element={<CompanyProfile />} />
              <Route path="/whitelabel/branding/domain" element={<CompanyProfile />} />
              {/* <Route path="/whitelabel/upgrade-plan" element={<UpgradePlansPage />} /> */}
              <Route path="/whitelabel/profile" element={<AdminProfile />} />
              <Route path="/whitelabel/profile/password" element={<AdminProfile />} />
              <Route path="/whitelabel/profile/security" element={<SecuritySettings />} />
              <Route path="/whitelabel/*" element={<Navigate to="/whitelabel/dashboard" replace />} />
            </Route>

            {/* Employee Routes (Placeholders) */}
            <Route element={<DashboardLayout allowedRoles={["employee"]} />}>
              <Route path="/employee/dashboard" element={<EmployeeDashboard />} />
              <Route path="/employee/dashboard/assigned" element={<EmployeeDashboard />} />
              <Route path="/employee/dashboard/tasks" element={<EmployeeDashboard />} />
              <Route path="/employee/dashboard/performance" element={<EmployeeDashboard />} />
              <Route path="/employee/commission" element={<EmployeeCommission />} />

              <Route path="/employee/leads" element={<EmployeeLeads />} />
              <Route path="/employee/leads/:leadId" element={<LeadDetailsPage />} />
              <Route path="/employee/leads/followup" element={<EmployeeFollowUpLeads />} />
              <Route path="/employee/leads/rejected" element={<EmployeeRejectedLeads />} />


              <Route path="/employee/loans" element={<EmployeeLoans />} />
              <Route path="/employee/loans/:id" element={<LoanDetailsPage />} />
              <Route path="/employee/loans/processing" element={<EmployeeProcessingLoans />} />
              <Route path="/employee/loans/approved" element={<EmployeeApprovedLoans />} />
              <Route path="/employee/loans/disbursed" element={<EmployeeDisbursedLoans />} />
              <Route path="/employee/loans/rejected" element={<EmployeeRejectedLoans />} />
              <Route path="/employee/apply/products" element={<AgentLoanProducts roleBasePath="/employee" />} />
              <Route path="/employee/apply/home-loan" element={<AgentHomeLoan roleBasePath="/employee" />} />
              <Route path="/employee/apply/business-loan" element={<AgentBusinessLoan roleBasePath="/employee" />} />
              <Route path="/employee/apply/personal-loan" element={<AgentPersonalLoan roleBasePath="/employee" />} />
              <Route path="/employee/apply/car-loan" element={<AgentCarLoan roleBasePath="/employee" />} />
              <Route path="/employee/apply/education-loan" element={<AgentEducationLoan roleBasePath="/employee" />} />

              {/* Agents (Visible if Manager/Permissions) */}
              <Route path="/employee/agents" element={<AllAgents />} />
              <Route path="/employee/agents/:id/view" element={<ViewAgentPage />} />
              <Route path="/employee/agents/performance" element={<AgentPerformance />} />
              <Route path="/employee/agents/commission" element={<Navigate to="/employee/commission" replace />} />
              <Route path="/employee/agents/targets" element={<AgentTargets />} />

              {/* Employees (Visible if Manager/Permissions) */}
              <Route path="/employee/employees" element={<AllEmployees />} />
              <Route path="/employee/employees/:id/view" element={<ViewEmployeePage />} />
              <Route path="/employee/employees/logs" element={<EmployeeActivityLogs />} />

              <Route path="/employee/documents/*" element={<EmployeeDocuments />} />
              <Route path="/employee/followups/*" element={<EmployeeFollowUps />} />
              <Route path="/employee/reports/performance" element={<EmployeePerformanceReport />} />
              <Route path="/employee/reports/files" element={<EmployeeFilesReport />} />
              <Route path="/employee/alerts/*" element={<EmployeeAlerts />} />
              <Route path="/employee/profile/*" element={<EmployeeProfile />} />
              <Route path="/employee/*" element={<Navigate to="/employee/dashboard" replace />} />
            </Route>

            {/* Agent Routes (Placeholders) */}
            <Route element={<DashboardLayout allowedRoles={["agent"]} />}>
              <Route path="/agent/dashboard" element={<AgentDashboard />} />
              <Route path="/agent/dashboard/leads" element={<AgentDashboard />} />
              <Route path="/agent/dashboard/earnings" element={<AgentDashboard />} />
              <Route path="/agent/dashboard/performance" element={<AgentDashboard />} />

              <Route path="/agent/leads" element={<AgentLeads />} />
              <Route path="/agent/leads/add" element={<AgentAddLead />} />
              <Route path="/agent/leads/:leadId" element={<LeadDetailsPage />} />
              <Route path="/agent/leads/followup" element={<AgentFollowUpLeads />} />
              <Route path="/agent/leads/pending" element={<PendingLeads />} />
              <Route path="/agent/leads/converted" element={<ConvertedLeads />} />
              <Route path="/agent/leads/rejected" element={<AgentRejectedLeads />} />
              <Route path="/agent/loans" element={<AgentLoans />} />
              <Route path="/agent/loans/:id" element={<AgentLoanDetails />} />
              <Route path="/agent/loans/processing" element={<AgentProcessingLoans />} />
              <Route path="/agent/loans/approved" element={<AgentApprovedLoans />} />
              <Route path="/agent/loans/disbursed" element={<AgentDisbursedLoans />} />
              <Route path="/agent/loans/rejected" element={<AgentRejectedLoans />} />
              <Route path="/agent/documents/*" element={<AgentDocuments />} />
              <Route path="/agent/earnings/*" element={<AgentEarnings />} />
              <Route path="/agent/payouts/*" element={<AgentPayouts />} />
              <Route path="/agent/followups/*" element={<AgentFollowUps />} />
              <Route path="/agent/notifications/*" element={<AgentNotifications />} />
              <Route path="/agent/profile/*" element={<AgentProfile />} />

              {/* Agent Applied Loan Forms */}
              <Route path="/agent/apply/products" element={<AgentLoanProducts />} />
              <Route path="/agent/apply/home-loan" element={<AgentHomeLoan />} />
              <Route path="/agent/apply/business-loan" element={<AgentBusinessLoan />} />
              <Route path="/agent/apply/personal-loan" element={<AgentPersonalLoan />} />
              <Route path="/agent/apply/car-loan" element={<AgentCarLoan />} />
              <Route path="/agent/apply/education-loan" element={<AgentEducationLoan />} />
              <Route path="/agent/*" element={<Navigate to="/agent/dashboard" replace />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
